import { ReactNode, useRef, useState } from 'react';

import CardStack from '../../../components/CardStack/CardStack';
import Form, { FormHandles } from '../../../components/Form/Form';
import ScrollToLastMounted from '../../../components/ScrollToLastMounted/ScrollToLastMounted';
import Stack from '../../../components/Stack/Stack';
import Title from '../../../components/Title/Title';
import UncaughtErrors from '../../../components/UncaughtErrors/UncaughtErrors';
import useTrans from '../../../hooks/ui/useTrans';
import FormDimmableCard from '../../components/FormDimmableCard/FormDimmableCard';
import usePreview from '../../hooks/usePreview';
import BoatModelCategory from '../../types/BoatModelCategory';
import BoatModelWithMedia from '../../types/BoatModelWithMedia';

import BoatModelFilters from './components/BoatModelFilters/BoatModelFilters';
import BoatModelFiltersCard from './components/BoatModelFiltersCard/BoatModelFiltersCard';
import BoatModelInfoModal from './components/BoatModelInfoModal/BoatModelInfoModal';
import BoatModelsPerCategory from './components/BoatModelsPerCategory/BoatModelsPerCategory';
import BookingParameterFields from './components/BookingParameterFields/BookingParameterFields';
import CannotContinueNote from './components/CannotContinueNote/CannotContinueNote';
import PickupDateCard from './components/PickupDateCard/PickupDateCard';
import determineIfCustomerWantsMultipleDays from './helpers/determineIfCustomerWantsMultipleDays';
import useFetchDates from './hooks/useFetchDates';
import useRequestedDates from './hooks/useRequestedDates';
import BookingFormLayout from './layouts/BookingFormLayout';
import ComposeBookingLayout from './layouts/ComposeBookingLayout';
import ComposeBookingProps from './types/pageProps/ComposeBookingProps';

type BoatModelViewProps = {
    categories: Record<string, BoatModelCategory>;
    boatModels: Record<string, BoatModelWithMedia> | null;
    enableFilters: boolean;
} & ComposeBookingProps;

const BoatModelView = ({
    boatModels,
    categories,
    parameters = {},
    nextStepUrl,
    uiParameters,
    enableFilters,
    errors,
}: BoatModelViewProps) => {
    const trans = useTrans('CustomerArea.BookingForm.BoatModel.');
    const preview = usePreview<BoatModelWithMedia>({});
    const [dimCards, setDimCards] = useState(false);
    const [minimumPassengers, setMinimumPassengers] = useState<number>();
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>();

    const formRef = useRef<FormHandles>(null);
    const fetchDates = useFetchDates(nextStepUrl, parameters);
    const requestedDates = useRequestedDates(parameters);

    const selectBoatModel = (
        boatModel: BoatModelWithMedia,
        quantity: number
    ) => {
        preview.cancel();

        formRef.current?.submit({
            boatModelId: boatModel.id,
            numberOfBoats: quantity,
        });
    };

    const categoriesWithBoats = Object.values(categories).filter((category) => {
        return (
            Object.values(boatModels || {}).filter(
                (boatModel) => boatModel.categoryId === category.id
            ).length > 0
        );
    });

    let visibleBoatModels = Object.values(boatModels ?? {});
    if (visibleBoatModels && uiParameters?.filterBoatModels) {
        visibleBoatModels = visibleBoatModels.filter(
            (value) => uiParameters.filterBoatModels.indexOf(value.id) !== -1
        );
    }

    const hasParameters = parameters.dockId || parameters.boatModelId;

    const customerWantsMultipleDays =
        determineIfCustomerWantsMultipleDays(requestedDates);

    return (
        <Form
            method="get"
            action={nextStepUrl}
            formRef={formRef}
            inertiaOptions={{ preserveScroll: true }}
        >
            <BookingParameterFields parameters={parameters} />

            <ScrollToLastMounted offset={-100}>
                <CardStack noDecoration>
                    <PickupDateCard
                        isFirst={false}
                        value={requestedDates}
                        onChange={fetchDates}
                        dimmed={preview.isPreviewing}
                        onInteractingWithDatePicker={setDimCards}
                    />

                    {enableFilters && (
                        <BoatModelFiltersCard
                            title={trans('Title')}
                            dimmed={preview.isPreviewing}
                            isFirst={!hasParameters}
                            isLast={false}
                        >
                            <BoatModelFilters
                                categories={categoriesWithBoats}
                                selectedCategory={selectedCategoryId}
                                minimumPassengers={minimumPassengers}
                                onMinimumPassengersChange={setMinimumPassengers}
                                onCategoryChange={setSelectedCategoryId}
                            />
                        </BoatModelFiltersCard>
                    )}

                    {boatModels && visibleBoatModels && (
                        <FormDimmableCard
                            dimmed={preview.isPreviewing || dimCards}
                            isFirst={false}
                        >
                            <ScrollToLastMounted.Point
                                deps={[
                                    requestedDates?.pickupDate,
                                    requestedDates?.returnDate,
                                ]}
                            />

                            <Stack>
                                {!enableFilters && (
                                    <Title variant="area">
                                        {trans('Title')}
                                    </Title>
                                )}

                                {Object.values(visibleBoatModels).length ? (
                                    <BoatModelsPerCategory
                                        boatModels={visibleBoatModels}
                                        categories={categoriesWithBoats}
                                        onPreview={preview.start}
                                        onSelect={selectBoatModel}
                                        minimumPassengers={minimumPassengers}
                                        categoryId={selectedCategoryId}
                                        customerWantsMultipleDays={
                                            customerWantsMultipleDays
                                        }
                                    />
                                ) : (
                                    <CannotContinueNote>
                                        {trans('NoBoatsForDate')}
                                    </CannotContinueNote>
                                )}
                                <UncaughtErrors
                                    errors={errors}
                                    exclude={[
                                        'username',
                                        'password',
                                        'requestedPickupDate',
                                        'requestedReturnDate',
                                        'couponCode',
                                        'customerTypeId',
                                    ]}
                                />
                            </Stack>
                        </FormDimmableCard>
                    )}
                </CardStack>
            </ScrollToLastMounted>

            {preview.current && (
                <BoatModelInfoModal
                    boatModel={preview.current}
                    onSelect={selectBoatModel}
                    onCloseRequested={preview.cancel}
                />
            )}
        </Form>
    );
};

BoatModelView.layout = (page: ReactNode) => (
    <BookingFormLayout titleTranslationKey="CustomerArea.BookingForm.BoatModel.Title">
        <ComposeBookingLayout currentStep="boatModel">
            {page}
        </ComposeBookingLayout>
    </BookingFormLayout>
);

export default BoatModelView;
